import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import charactersData from '../story/the-missing-hour/characters_data.json';
import evidenceData from '../story/the-missing-hour/evidence_data.json';
import Header from './Header';
import BottomNavigation from './BottomNavigation';
import evidenceApi from '../services/evidenceApi';
import CaseFile from './CaseFile';

// Evidence items awaiting submission based on witness statements and forensic analysis
const evidenceAwaitingSubmission = [
  {
    id: "E1",
    title: "Vehicle Spotted Near Crime Scene",
    description: "Witness statement: White van seen in the area around the time of the incident. Vehicle was parked near the farmhouse access road between 2:00-3:00 PM on the day of the murder. Driver was observed acting suspiciously.",
    evidenceType: "vehicle",
    priority: "high",
    investigationDay: 1,
    status: "awaiting_submission",
    whatToLookFor: "White van, any markings or signage, driver behavior"
  },
  {
    id: "E2",
    title: "Weapon Used in Attack",
    description: "Forensic analysis of victim's wounds indicates blunt force trauma to the head and defensive wounds on arms. Victim also shows signs of strangulation. Weapon likely heavy object or hands/forearms.",
    evidenceType: "weapon",
    priority: "urgent",
    investigationDay: 1,
    status: "awaiting_submission",
    whatToLookFor: "Heavy object, tool, or weapon capable of blunt force"
  },
  {
    id: "E3",
    title: "Clothing/Fabric Evidence",
    description: "Witness statement: Victim was last seen wearing blue jeans and a red plaid shirt. These items are missing from the crime scene and may contain crucial evidence.",
    evidenceType: "clothing",
    priority: "medium",
    investigationDay: 1,
    status: "awaiting_submission",
    whatToLookFor: "Blue jeans, red plaid shirt, any fabric with damage"
  }
];

// Transform story evidence data to match our archive display format
const transformEvidenceData = (evidence) => {
  return evidence.map((item, index) => {
    // Generate a relevance score based on forensic status and investigation day
    let relevanceScore = 5; // Default score
    if (item.forensicStatus === 'match') relevanceScore = 8;
    else if (item.forensicStatus === 'pending') relevanceScore = 6;
    else if (item.forensicStatus === 'inconclusive') relevanceScore = 4;
    
    // Add priority based on evidence type and status
    let priority = 'medium';
    if (item.evidenceType === 'biological' || item.evidenceType === 'physical') priority = 'high';
    if (item.forensicStatus === 'pending' && item.evidenceType === 'biological') priority = 'urgent';
    
    // Format investigation day for display
    const investigationDate = new Date(2024, 0, 15 + (item.investigationDay - 1));
    const formattedDate = investigationDate.toISOString().split('T')[0];
    
    return {
      id: item.id,
      evidenceNumber: item.id,
      title: item.description,
      evidenceType: item.evidenceType.charAt(0).toUpperCase() + item.evidenceType.slice(1).replace('_', ' '),
      submittedDate: formattedDate,
      submittedTime: "09:00", // Default time since not in original data
      location: item.foundLocation,
      submitterName: "Investigation Team", // Default since not in original data
      status: item.forensicStatus.charAt(0).toUpperCase() + item.forensicStatus.slice(1),
      relevanceScore: relevanceScore,
      priority: priority,
      investigationDay: item.investigationDay,
      originalData: item, // Keep original data for detailed view
      aiAnalysis: {
        evidenceReview: item.notes || "Evidence collected and logged for forensic analysis.",
        storyAlignment: `This evidence was discovered on investigation day ${item.investigationDay} and is currently ${item.forensicStatus}.`,
        investigativeValue: `Evidence type: ${item.evidenceType}. Status: ${item.forensicStatus}.`,
        recommendedActions: ["Forensic analysis", "Documentation", "Chain of custody verification"],
        imageAnalysis: item.photographs ? "Photographs available for analysis." : "No photographs currently available."
      },
      tags: item.tags || []
    };
  });
};

// Use the transformed evidence data from the story
const submittedEvidence = transformEvidenceData(evidenceData.evidence);

// Modal component
const EvidenceModal = ({ isOpen, onClose, title, items, onSelect, selectedValue }) => {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to restore scroll when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleClose = () => {
    onClose();
  };

  const handleItemSelect = (item) => {
    onSelect(item.description || item.name);
    onClose(); // Close modal after selection
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button 
            className="modal-close" 
            onClick={handleClose}
            aria-label="Close modal"
            type="button"
          >
            ×
          </button>
        </div>
        <div className="modal-body">
          {items.map((item, index) => (
            <div
              key={index}
              className={`modal-item ${selectedValue === (item.description || item.name) ? 'selected' : ''}`}
              onClick={() => handleItemSelect(item)}
            >
              <span className="modal-item-text">
                {item.description || item.name}
              </span>
              {item.evidenceType && (
                <span className="modal-item-type">
                  {item.evidenceType}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Database = ({ activeTab = 'evidence', setActiveTab }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Use local state if no props are provided (fallback for standalone usage)
  const [localActiveTab, setLocalActiveTab] = useState('evidence');
  const currentActiveTab = activeTab || localActiveTab;
  const currentSetActiveTab = setActiveTab || setLocalActiveTab;
  
  // Evidence submission state
  const [showAddEvidenceModal, setShowAddEvidenceModal] = useState(false);
  const [evidenceSubmission, setEvidenceSubmission] = useState({
    evidence_type: '',
    evidence_name: '',
    additional_notes: '',
    image_file: null
  });
  const [isSubmittingEvidence, setIsSubmittingEvidence] = useState(false);
  const [evidenceReviewResult, setEvidenceReviewResult] = useState(null);
  
  // Suspect carousel state
  const [currentSuspectIndex, setCurrentSuspectIndex] = useState(0);
  
  // Evidence carousel state
  const [evidenceCarouselIndex, setEvidenceCarouselIndex] = useState(0);
  
  // Evidence archive search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTypeFilter, setSelectedTypeFilter] = useState('all');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('all');

  // Handle initial hash navigation and evidence anchor navigation
  useEffect(() => {
    // Only run this effect once on mount or when location.state changes
    if (window.location.hash) {
      const hash = window.location.hash.substring(1);
      console.log('Initial hash navigation:', hash);
      
      // Handle tab switching via hash
      if (hash === 'suspects' && currentActiveTab !== 'suspects') {
        console.log('Switching to suspects tab via hash');
        currentSetActiveTab('suspects');
      } else if (hash === 'casefile' && currentActiveTab !== 'casefile') {
        console.log('Switching to casefile tab via hash');
        currentSetActiveTab('casefile');
      } else if (hash === 'evidence' && currentActiveTab !== 'evidence') {
        console.log('Switching to evidence tab via hash');
        currentSetActiveTab('evidence');
      } else if (hash.startsWith('E') && hash.length > 1) {
        // Handle evidence-specific hash (e.g., #E1, #E2)
        console.log('Evidence-specific hash detected:', hash);
        if (currentActiveTab !== 'evidence') {
          currentSetActiveTab('evidence');
        }
        
        // Scroll to the specific evidence item with multiple attempts to ensure it's rendered
        const scrollToEvidence = () => {
          const evidenceElement = document.getElementById(hash);
          if (evidenceElement) {
            console.log('Evidence element found, scrolling to:', hash);
            
            // Remove any existing highlights first
            document.querySelectorAll('.evidence-archive-item.highlighted').forEach(item => {
              item.classList.remove('highlighted');
            });
            
            // Add highlight class to this specific evidence item
            evidenceElement.classList.add('highlighted');
            
            evidenceElement.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center' 
            });
            
            // Remove highlight after 3 seconds
            setTimeout(() => {
              evidenceElement.classList.remove('highlighted');
            }, 3000);
            
            return true;
          }
          return false;
        };
        
        // Try immediately, then with delays to ensure rendering
        if (!scrollToEvidence()) {
          setTimeout(() => {
            if (!scrollToEvidence()) {
              setTimeout(() => {
                scrollToEvidence();
              }, 200);
            }
          }, 100);
        }
      }
    }
  }, [location.state]); // Only depend on location.state, not on currentActiveTab or hash
  
  // Add/remove case-file-active class to body element
  useEffect(() => {
    if (currentActiveTab === 'casefile') {
      document.body.classList.add('case-file-active');
    } else {
      document.body.classList.remove('case-file-active');
    }

    // Cleanup function to remove class when component unmounts
    return () => {
      document.body.classList.remove('case-file-active');
    };
  }, [currentActiveTab]);

  
  // No longer need evidence archive state - using dedicated page
  
  // Get active suspects from characters
  const activeSuspects = charactersData.characters.filter(character => 
    character.role === 'suspect' || character.role === 'person_of_interest'
  );

  // Get unique evidence types and statuses for filters
  const evidenceTypes = ['all', ...new Set(submittedEvidence.map(evidence => evidence.evidenceType))];
  const evidenceStatuses = ['all', ...new Set(submittedEvidence.map(evidence => evidence.status))];

  // Clear all caches manually
  const clearAllCaches = async () => {
    try {
      // Clear service worker caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
        console.log('All caches cleared manually');
        alert('All caches cleared! Please refresh the page.');
      }
      
      // Clear localStorage and sessionStorage
      localStorage.clear();
      sessionStorage.clear();
      console.log('Storage cleared manually');
      
      // Force reload if service worker is active
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (let registration of registrations) {
          await registration.unregister();
          console.log('Service worker unregistered manually');
        }
      }
    } catch (error) {
      console.log('Manual cache clearing error:', error);
    }
  };

  // Get evidence type icon
  const getEvidenceTypeIcon = (type) => {
    switch (type.toLowerCase()) {
      case 'vehicle':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5H6.5C5.84 5 5.29 5.42 5.08 6.01L3 12V20C3 20.55 3.45 21 4 21H5C5.55 21 6 20.55 6 20V19H18V20C18 20.55 18.45 21 19 21H20C20.55 21 21 20.55 21 20V12L18.92 6.01ZM6.5 16C5.67 16 5 15.33 5 14.5C5 13.67 5.67 13 6.5 13C7.33 13 8 13.67 8 14.5C8 15.33 7.33 16 6.5 16ZM17.5 16C16.67 16 16 15.33 16 14.5C16 13.67 16.67 13 17.5 13C18.33 13 19 13.67 19 14.5C19 15.33 18.33 16 17.5 16ZM5 11L6.5 6.5H17.5L19 11H5Z" fill="#8B0000"/>
          </svg>
        );
      case 'weapon':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6.92 19H17.08L19.5 7H4.5L6.92 19ZM7.5 17L6.04 9H17.96L16.5 17H7.5Z" fill="#8B0000"/>
            <path d="M12 2L13.5 7H10.5L12 2Z" fill="#8B0000"/>
            <path d="M8 7H16L15 9H9L8 7Z" fill="#8B0000"/>
          </svg>
        );
      case 'clothing':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1H5C3.89 1 3 1.89 3 3V7L9 13V22L15 22V13L21 9ZM19 8.82L15 12.82V20H9V12.82L5 8.82V3H15L19 8.82Z" fill="#8B0000"/>
          </svg>
        );
      case 'physical':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z" fill="#8B0000"/>
            <path d="M19 15L19.5 16.5L21 17L19.5 17.5L19 19L18.5 17.5L17 17L18.5 16.5L19 15Z" fill="#8B0000"/>
            <path d="M5 15L5.5 16.5L7 17L5.5 17.5L5 19L4.5 17.5L3 17L4.5 16.5L5 15Z" fill="#8B0000"/>
          </svg>
        );
      case 'biological':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="#8B0000" strokeWidth="2" fill="none"/>
            <path d="M8 12C8 9.79 9.79 8 12 8C14.21 8 16 9.79 16 12C16 14.21 14.21 16 12 16C9.79 16 8 14.21 8 12Z" fill="#8B0000"/>
            <path d="M12 2V6M12 18V22M22 12H18M6 12H2" stroke="#8B0000" strokeWidth="2"/>
          </svg>
        );
      case 'trace':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 21L9 15L12 18L21 9L19.59 7.59L12 15.17L9 12.17L4.41 16.76L3 21Z" fill="#8B0000"/>
            <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z" fill="#8B0000"/>
          </svg>
        );
      case 'digital':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="4" y="4" width="16" height="16" rx="2" stroke="#8B0000" strokeWidth="2" fill="none"/>
            <rect x="8" y="8" width="8" height="8" rx="1" fill="#8B0000"/>
            <circle cx="12" cy="12" r="1" fill="white"/>
          </svg>
        );
      case 'documentary':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" stroke="#8B0000" strokeWidth="2" fill="none"/>
            <path d="M14 2V8H20" stroke="#8B0000" strokeWidth="2"/>
            <path d="M16 13H8" stroke="#8B0000" strokeWidth="2"/>
            <path d="M16 17H8" stroke="#8B0000" strokeWidth="2"/>
            <path d="M10 9H8" stroke="#8B0000" strokeWidth="2"/>
          </svg>
        );
      case 'audio visual':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="2" y="6" width="20" height="12" rx="2" stroke="#8B0000" strokeWidth="2" fill="none"/>
            <path d="M2 10H22" stroke="#8B0000" strokeWidth="2"/>
            <circle cx="12" cy="12" r="3" stroke="#8B0000" strokeWidth="2" fill="none"/>
            <path d="M12 9V15M9 12H15" stroke="#8B0000" strokeWidth="2"/>
          </svg>
        );
      default:
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="3" width="18" height="18" rx="2" stroke="#8B0000" strokeWidth="2" fill="none"/>
            <path d="M9 9H15V15H9V9Z" fill="#8B0000"/>
          </svg>
        );
    }
  };

  // Filter evidence based on search and filters
  const filteredEvidence = submittedEvidence.filter(evidence => {
    const matchesSearch = evidence.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         evidence.location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = selectedTypeFilter === 'all' || evidence.evidenceType === selectedTypeFilter;
    const matchesStatus = selectedStatusFilter === 'all' || evidence.status === selectedStatusFilter;
    
    return matchesSearch && matchesType && matchesStatus;
  });

  // Handle navigation state to set active tab
  useEffect(() => {
    if (location.state && location.state.activeTab) {
      setLocalActiveTab(location.state.activeTab);
      if (currentSetActiveTab) {
        currentSetActiveTab(location.state.activeTab);
      }
    }
  }, [location.state, currentSetActiveTab]);
  

  
  const handleSuspectClick = (suspectId) => {
    navigate(`/suspect/${suspectId}`);
  };
  
  const handleSuspectNavigation = (direction) => {
    if (direction === 'prev' && currentSuspectIndex > 0) {
      setCurrentSuspectIndex(currentSuspectIndex - 1);
    } else if (direction === 'next' && currentSuspectIndex < activeSuspects.length - 1) {
      setCurrentSuspectIndex(currentSuspectIndex + 1);
    }
  };
  
  // Touch/swipe support
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };
  
  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    
    if (isLeftSwipe && currentSuspectIndex < activeSuspects.length - 1) {
      setCurrentSuspectIndex(currentSuspectIndex + 1);
    } else if (isRightSwipe && currentSuspectIndex > 0) {
      setCurrentSuspectIndex(currentSuspectIndex - 1);
    }
    
    setTouchStart(null);
    setTouchEnd(null);
  };



  const handleEvidenceNavigation = (direction) => {
    if (direction === 'prev' && evidenceCarouselIndex > 0) {
      setEvidenceCarouselIndex(evidenceCarouselIndex - 1);
    } else if (direction === 'next' && evidenceCarouselIndex < evidenceAwaitingSubmission.length - 1) {
      setEvidenceCarouselIndex(evidenceCarouselIndex + 1);
    }
  };

  // Evidence submission handlers
  const handleEvidenceInputChange = (field, value) => {
    setEvidenceSubmission(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setEvidenceSubmission(prev => ({
        ...prev,
        image_file: file
      }));
    }
  };

  const handleSubmitEvidence = async () => {
    try {
      setIsSubmittingEvidence(true);
      
      // Validate evidence data
      const validationErrors = evidenceApi.validateEvidenceData(evidenceSubmission);
      if (validationErrors.length > 0) {
        alert('Validation errors: ' + validationErrors.join(', '));
        return;
      }

      // Prepare evidence data for submission
      const evidenceData = {
        evidence_type: evidenceSubmission.evidence_type,
        evidence_name: evidenceSubmission.evidence_name,
        additional_notes: evidenceSubmission.additional_notes,
        story_context: evidenceApi.buildStoryContext('the-missing-hour', evidenceSubmission.evidence_type)
      };

      // Add image data if present
      if (evidenceSubmission.image_file) {
        evidenceData.image_data = await evidenceApi.imageToBase64(evidenceSubmission.image_file);
      }

      // Submit to API
      const result = await evidenceApi.submitEvidence(evidenceData);
      setEvidenceReviewResult(result);
      
      // Reset form
      setEvidenceSubmission({
        evidence_type: '',
        evidence_name: '',
        additional_notes: '',
        image_file: null
      });
      
    } catch (error) {
      console.error('Evidence submission failed:', error);
      alert('Evidence submission failed: ' + error.message);
    } finally {
      setIsSubmittingEvidence(false);
    }
  };

  const resetEvidenceForm = () => {
    setEvidenceSubmission({
      evidence_type: '',
      evidence_name: '',
      additional_notes: '',
      image_file: null
    });
    setEvidenceReviewResult(null);
    setShowAddEvidenceModal(false);
    
    // Re-enable body scrolling when modal closes
    document.body.style.overflow = 'auto';
  };

  // Evidence archive functions
  const handleViewEvidenceDetail = (evidenceId) => {
    console.log('Navigating to evidence:', evidenceId);
    
    // Navigate to evidence detail page
    navigate(`/evidence/${evidenceId}`, {
      state: { 
        activeTab: 'database', 
        databaseTab: 'evidence'
      }
    });
  };

  // Handle modal opening with scroll management
  const handleOpenEvidenceModal = () => {
    setShowAddEvidenceModal(true);
    
    // Disable background scrolling
    document.body.style.overflow = 'hidden';
  };

  // Cleanup body scroll when component unmounts
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  // Note: Scroll management is only for page navigation, not modal interactions







  return (
    <div 
      className={`database-container ${currentActiveTab === 'casefile' ? 'case-file-active' : ''}`}
      style={currentActiveTab === 'casefile' ? {
        backgroundImage: 'url(/images/bg-courtroom.jpeg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed'
      } : {}}
    >
      <Header />
      <div className="database-tabs">
        <button 
          className={`tab-button ${currentActiveTab === 'evidence' ? 'active' : ''}`}
          onClick={() => currentSetActiveTab('evidence')}
        >
          Evidence
        </button>
        <button 
          className={`tab-button ${currentActiveTab === 'suspects' ? 'active' : ''}`}
          onClick={() => currentSetActiveTab('suspects')}
        >
          Suspects
        </button>
        <button 
          className={`tab-button ${currentActiveTab === 'casefile' ? 'active' : ''}`}
          onClick={() => currentSetActiveTab('casefile')}
        >
          Case File
        </button>
      </div>

      {/* Evidence Database */}
      {currentActiveTab === 'evidence' && (
        <div className="evidence-database">
          <div className="page-title-section">
            <h2>Evidence Database</h2>
          </div>
          
          {/* Evidence Awaiting Submission */}
          <div className="evidence-awaiting-submission">
            <div style={{ fontFamily: "'Arial Narrow', Arial, sans-serif", marginBottom: "20px" }}>
              <p>Review the witness statements below, then go out and take photos of possible evidence. Submit your photos for forensic analysis - you'll receive a report indicating if it's a good match or not.</p>
            </div>
            
            <div className="evidence-items-grid">
              <div 
                className="evidence-carousel-container"
                style={{
                  transform: `translateX(-${evidenceCarouselIndex * 100}%)`,
                  transition: 'transform 0.3s ease'
                }}
              >
                {evidenceAwaitingSubmission.map((evidence) => (
                  <div 
                    key={evidence.id} 
                    className="evidence-item-card"
                    style={{
                      backgroundImage: evidence.evidenceType === 'vehicle' 
                        ? 'url(/images/bg-evidence-vehicle.jpeg)' 
                        : evidence.evidenceType === 'weapon'
                        ? 'url(/images/bg-evidence-weapon.jpeg)'
                        : evidence.evidenceType === 'clothing'
                        ? 'url(/images/bg-evidence-clothing.jpeg)'
                        : 'none',
                      backgroundSize: 'cover',
                      backgroundPosition: 'bottom center',
                      backgroundRepeat: 'no-repeat'
                    }}
                  >
                    <div className="evidence-submission-header">
                      <h5>{evidence.title}</h5>
                      <span className={`priority-badge ${evidence.priority}`}>
                        {evidence.priority.toUpperCase()}
                      </span>
                    </div>
                    
                    <div className="evidence-item-content">
                      <p className="evidence-submission-description">{evidence.description}</p>
                      
                      <div className="detail-section">
                        <strong>What to Look For:</strong>
                        <p>{evidence.whatToLookFor}</p>
                      </div>
                    </div>
                    
                    <div className="evidence-submission-actions">
                      <button 
                        className="submit-evidence-btn primary"
                        onClick={() => {
                          setEvidenceSubmission(prev => ({
                            ...prev,
                            evidence_type: evidence.evidenceType,
                            evidence_name: evidence.title,
                            additional_notes: evidence.whatToLookFor
                          }));
                          handleOpenEvidenceModal();
                        }}
                      >
                        TAKE PHOTO / UPLOAD EVIDENCE
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Evidence Carousel Legend */}
              {evidenceAwaitingSubmission.length > 1 && (
                <div className="evidence-carousel-legend">
                  <span className="legend-text">{evidenceCarouselIndex + 1} of {evidenceAwaitingSubmission.length}</span>
                </div>
              )}
              
              {/* Evidence Navigation Arrows */}
              {evidenceAwaitingSubmission.length > 1 && (
                <>
                  <button 
                    className="evidence-carousel-nav prev"
                    onClick={() => handleEvidenceNavigation('prev')}
                    disabled={evidenceCarouselIndex === 0}
                  >
                    ‹
                  </button>
                  <button 
                    className="evidence-carousel-nav next"
                    onClick={() => handleEvidenceNavigation('next')}
                    disabled={evidenceCarouselIndex === evidenceAwaitingSubmission.length - 1}
                  >
                    ›
                  </button>
                </>
              )}
            </div>
          </div>
          
          {/* Evidence Archive */}
          <div className="evidence-archive-section">
            <h4>Evidence Archive</h4>
            <div style={{ fontFamily: "'Arial Narrow', Arial, sans-serif", marginBottom: "20px" }}>
              <p>Browse previously submitted evidence and view forensic analysis results. Use the filters below to search by type or status.</p>
            </div>
            
            {/* Search and Filter Controls */}
            <div className="evidence-search-filters">
              <div className="search-box">
                <input
                  type="text"
                  placeholder="Search evidence by title or location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>
              
              <div className="filter-pills">
                <div className="filter-group">
                  <label>Type:</label>
                  <div className="pill-container">
                    {evidenceTypes.map((type) => {
                      const count = type === 'all' 
                        ? submittedEvidence.length 
                        : submittedEvidence.filter(evidence => evidence.evidenceType === type).length;
                      
                      return (
                        <button
                          key={type}
                          className={`filter-pill ${selectedTypeFilter === type ? 'active' : ''}`}
                          onClick={() => setSelectedTypeFilter(type)}
                        >
                          {type === 'all' ? 'All Types' : type} ({count})
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                <div className="filter-group">
                  <label>Status:</label>
                  <div className="pill-container">
                    {evidenceStatuses.map((status) => {
                      const count = status === 'all' 
                        ? submittedEvidence.length 
                        : submittedEvidence.filter(evidence => evidence.status === status).length;
                      
                      return (
                        <button
                          key={status}
                          className={`filter-pill ${selectedStatusFilter === status ? 'active' : ''}`}
                          onClick={() => setSelectedStatusFilter(status)}
                        >
                          {status === 'all' ? 'All Statuses' : status} ({count})
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="evidence-archive-list">
              {filteredEvidence.map((evidence) => (
                <div 
                  key={evidence.id} 
                  id={evidence.id}
                  className="evidence-archive-item"
                  onClick={() => handleViewEvidenceDetail(evidence.id)}
                  style={{ cursor: 'pointer' }}
                  data-evidence-id={evidence.id}
                >
                  <div className="evidence-item-icon">
                    {getEvidenceTypeIcon(evidence.evidenceType)}
                  </div>
                  
                  <div className="evidence-item-main">
                    <div className="evidence-item-header">
                      <div className="evidence-meta">
                        <span className="evidence-number">{evidence.evidenceNumber}</span>
                        <span className="evidence-date">{evidence.submittedDate}</span>
                      </div>
                      <div className={`status-badge ${evidence.status.toLowerCase().replace(/\s+/g, '-')}`}>
                        {evidence.status}
                      </div>
                    </div>
                    
                    <h5 className="evidence-title">{evidence.title}</h5>
                  </div>
                  

                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Suspect List */}
      {currentActiveTab === 'suspects' && (
        <div className="suspects-carousel">
          <div 
            className="suspects-carousel-container"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {activeSuspects.length > 0 ? activeSuspects.map((suspect, index) => (
                              <div 
                  key={suspect.id} 
                  className={`suspect-card ${currentSuspectIndex === index ? 'active' : ''}`}
                  style={{
                    backgroundImage: `url("${suspect.profile_photo || `/story/the-missing-hour/suspects/${suspect.id}_mugshot.jpg`}")`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    transform: `translateX(${(index - currentSuspectIndex) * 100}%)`,
                    backgroundColor: '#333'
                  }}
                  title={`Image: ${suspect.profile_photo || `/story/the-missing-hour/suspects/${suspect.id}_mugshot.jpg`}`}
                >
                <div className="suspect-overlay">
                  <div className="suspect-info">
                    <span className={`suspect-status ${suspect.status || 'arrested'}`}>
                      {suspect.status === 'ruled_out' ? 'RELEASED' : 
                       suspect.status === 'interviewed' ? 'INTERVIEWED' :
                       suspect.status === 'processing' ? 'PROCESSING' :
                       suspect.status === 'awaiting' ? 'AWAITING INTERVIEW' : 'ARRESTED'}
                    </span>
                    <h3 className="suspect-name">{suspect.name}</h3>
                    <div className="suspect-arrest-info">
                      <span className="arrest-date">Arrested: {suspect.arrested_date || 'Day 1 - 10:00 AM'}</span>
                    </div>
                    {suspect.status === 'interviewed' && suspect.interview_date && (
                      <span className="interview-date">Interviewed: {suspect.interview_date}</span>
                    )}
                  </div>
                  <button 
                    className="suspect-view-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSuspectClick(suspect.id);
                    }}
                  >
                    VIEW DETAILS
                  </button>
                </div>
              </div>
            )) : (
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                color: 'white',
                textAlign: 'center',
                fontSize: '24px'
              }}>
                No suspects found
              </div>
            )}
          </div>
          
          {/* Carousel Legend */}
          {activeSuspects.length > 0 && (
            <div className="carousel-legend">
              <span className="legend-text">{currentSuspectIndex + 1} of {activeSuspects.length}</span>
            </div>
          )}
          
          {/* Navigation Arrows */}
          {activeSuspects.length > 1 && (
            <>
              <button 
                className="carousel-nav prev"
                onClick={() => handleSuspectNavigation('prev')}
                disabled={currentSuspectIndex === 0}
              >
                ‹
              </button>
              <button 
                className="carousel-nav next"
                onClick={() => handleSuspectNavigation('next')}
                disabled={currentSuspectIndex === activeSuspects.length - 1}
              >
                ›
              </button>
            </>
          )}
        </div>
      )}

      {/* Case File */}
      {currentActiveTab === 'casefile' && (
        <CaseFile />
      )}

      {/* Evidence Submission Modal */}
      {showAddEvidenceModal && (
        <div className="modal-overlay" onClick={resetEvidenceForm}>
          <div className="evidence-modal-container" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>SUBMIT EVIDENCE FOR FORENSIC REVIEW</h3>
              <button className="modal-close" onClick={resetEvidenceForm}>×</button>
            </div>
            
            <div className="modal-body">
              {!evidenceReviewResult ? (
                // Evidence Submission Form
                <div className="evidence-submission-form">
                  {/* Evidence Item Being Submitted For */}
                  {evidenceSubmission.evidence_name && (
                    <div className="evidence-item-context">
                      <h4>Submitting Evidence For:</h4>
                      <div className="context-details">
                        <strong>{evidenceSubmission.evidence_name}</strong>
                        <p className="context-description">
                          {evidenceAwaitingSubmission.find(item => item.title === evidenceSubmission.evidence_name)?.description || ''}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  <div className="form-field">
                    <label>EVIDENCE TYPE *</label>
                    <select
                      name="evidence_type"
                      value={evidenceSubmission.evidence_type}
                      onChange={(e) => handleEvidenceInputChange('evidence_type', e.target.value)}
                      required
                    >
                      <option value="">Select evidence type...</option>
                      <option value="vehicle">Vehicle Evidence</option>
                      <option value="weapon">Weapon Evidence</option>
                      <option value="clothing">Clothing/Fabric Evidence</option>
                      <option value="physical">Physical Evidence</option>
                      <option value="digital">Digital Evidence</option>
                      <option value="biological">Biological Evidence</option>
                      <option value="trace">Trace Evidence</option>
                      <option value="documentary">Documentary Evidence</option>
                      <option value="audio_visual">Audio/Visual Evidence</option>
                    </select>
                  </div>

                  <div className="form-field">
                    <label>EVIDENCE NAME *</label>
                    <input
                      type="text"
                      value={evidenceSubmission.evidence_name}
                      onChange={(e) => handleEvidenceInputChange('evidence_name', e.target.value)}
                      placeholder="e.g., Blood-stained knife, CCTV footage, Soil sample..."
                      required
                    />
                  </div>

                  <div className="form-field">
                    <label>ADDITIONAL NOTES</label>
                    <textarea
                      value={evidenceSubmission.additional_notes}
                      onChange={(e) => handleEvidenceInputChange('additional_notes', e.target.value)}
                      placeholder="Describe the evidence, where it was found, any relevant details..."
                      rows="3"
                    />
                  </div>

                  <div className="form-field">
                    <label>EVIDENCE PHOTOGRAPH *</label>
                    <div className="evidence-photo-upload">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="file-upload-hidden"
                        id="evidence-photo-input"
                        required
                      />
                      
                      {!evidenceSubmission.image_file ? (
                        <>
                          <div className="evidence-photo-button" onClick={() => document.getElementById('evidence-photo-input').click()}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 15.2C13.7673 15.2 15.2 13.7673 15.2 12C15.2 10.2327 13.7673 8.8 12 8.8C10.2327 8.8 8.8 10.2327 8.8 12C8.8 13.7673 10.2327 15.2 12 15.2Z"/>
                          <path d="M9 2L7.17 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4H16.83L15 2H9ZM12 17C9.24 17 7 14.76 7 12C7 9.24 9.24 7 12 7C14.76 7 17 9.24 17 12C17 14.76 14.76 17 12 17Z"/>
                        </svg>
                        <span>CAPTURE EVIDENCE PHOTO</span>
                          </div>
                          <p className="evidence-photo-note">
                            Photographic evidence is required for forensic analysis. Max size: 5MB. Supported formats: JPG, PNG, GIF
                          </p>
                        </>
                      ) : (
                        <div className="evidence-photo-preview">
                          <img 
                            src={URL.createObjectURL(evidenceSubmission.image_file)} 
                            alt="Evidence photo preview" 
                            className="evidence-photo-image"
                          />
                          <div className="evidence-photo-actions">
                            <button 
                              type="button"
                              className="evidence-photo-cancel"
                              onClick={() => setEvidenceSubmission(prev => ({ ...prev, image_file: null }))}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                              </svg>
                              REMOVE PHOTO
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="form-actions">
                    <button 
                      className="control-button primary"
                      onClick={handleSubmitEvidence}
                      disabled={isSubmittingEvidence || !evidenceSubmission.evidence_type || !evidenceSubmission.evidence_name || !evidenceSubmission.image_file}
                    >
                      {isSubmittingEvidence ? 'SUBMITTING...' : 'SUBMIT FOR FORENSIC REVIEW'}
                    </button>
                  </div>
                </div>
              ) : (
                // Evidence Review Results
                <div className="evidence-review-results">
                  <div className="review-header">
                    <h4>EVIDENCE REVIEW COMPLETE</h4>
                    <div className="relevance-score">
                      Relevance Score: <span className="score-value">{evidenceReviewResult.relevance_score}/10</span>
                    </div>
                  </div>

                  <div className="review-section">
                    <h5>EVIDENCE ASSESSMENT</h5>
                    <p>{evidenceReviewResult.evidence_review}</p>
                  </div>

                  <div className="review-section">
                    <h5>STORY ALIGNMENT</h5>
                    <p>{evidenceReviewResult.story_alignment}</p>
                  </div>

                  <div className="review-section">
                    <h5>INVESTIGATIVE VALUE</h5>
                    <p>{evidenceReviewResult.investigative_value}</p>
                  </div>

                  {evidenceReviewResult.image_analysis && (
                    <div className="review-section">
                      <h5>IMAGE ANALYSIS</h5>
                      <p>{evidenceReviewResult.image_analysis}</p>
                    </div>
                  )}

                  <div className="review-section">
                    <h5>RECOMMENDED ACTIONS</h5>
                    <p>{evidenceReviewResult.recommended_actions}</p>
                  </div>

                  <div className="form-actions">
                    <button 
                      className="control-button primary"
                      onClick={resetEvidenceForm}
                    >
                      SUBMIT ANOTHER EVIDENCE
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      <BottomNavigation showNavigation={true} />
    </div>
  );
};

export default Database;
